/**
 * Outil « Multiplication en rectangle » (Boîte à outils, 6e → 5e).
 *
 * a × b = aire du rectangle a sur b, découpé selon les rangs des deux
 * facteurs (23 = 20 + 3) : chaque morceau est un produit partiel, la somme
 * des morceaux est le produit. À côté, la multiplication posée, dont chaque
 * ligne additionne une rangée de morceaux (même couleur). Décimaux (un
 * chiffre après la virgule) : le carré unité se découpe en 100 centièmes, et
 * l'on place la virgule. Calculatrice interdite sur l'exercice (`sendCost` >
 * 0) : la somme finale reste à faire.
 */

import { useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import type { ToolProps } from '../../tool-types';
import { Note, NumberField, Segmented } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { FIGURE_BG_CLASS, INK } from '../../ui/svg-parts';
import {
  commaText,
  distributeText,
  estimateText,
  factorText,
  plan,
  posed,
  productText,
  splitText,
  sumText,
  type Kind,
  type Plan,
} from './area';

const ROW_TONES = ['#93c5fd', '#fcd34d', '#86efac'];
const STROKE = '#1f2d55';
const W = 340;
const H = 300;
const X0 = 50;
const Y0 = 34;
const AW = 280;
const AH = 256;
const MIN_W = 36;
const MIN_H = 28;

/** tailles des morceaux : proportionnelles, sauf si un morceau serait trop fin */
function sizes(
  ps: readonly number[],
  scale: number,
  min: number,
): { px: number[]; exact: boolean } {
  const prop = ps.map((p) => p * scale);
  if (prop.every((x) => x >= min)) return { px: prop, exact: true };
  const total = prop.reduce((s, x) => s + x, 0);
  const small = prop.map((x) => x < min);
  const rest = total - small.filter(Boolean).length * min;
  const big = prop.reduce((s, x, i) => s + (small[i] ? 0 : x), 0);
  const px =
    rest >= min * small.filter((x) => !x).length
      ? prop.map((x, i) => (small[i] ? min : (x / big) * rest))
      : prop.map(() => Math.max(min, total / prop.length));
  return { px, exact: false };
}

function Rect({ pl, hide }: { pl: Plan; hide: boolean }) {
  // un seul morceau : son aire EST le produit ; calculatrice interdite → on ne l'écrit pas
  const single = pl.pieces.length === 1;
  const scale = Math.min(AW / pl.a, AH / pl.b);
  const cols = sizes(pl.aParts, scale, MIN_W);
  const rows = sizes(pl.bParts, scale, MIN_H);
  const exact = cols.exact && rows.exact;
  const xs = [X0];
  cols.px.forEach((w) => xs.push(xs.at(-1)! + w));
  const ys = [Y0];
  rows.px.forEach((h) => ys.push(ys.at(-1)! + h));
  const right = xs.at(-1)!;
  const bottom = ys.at(-1)!;
  // quadrillage (unités, ou dixièmes avec les unités en gras) quand on peut compter
  const grid = exact && pl.a <= 30 && pl.b <= 30;
  const k = pl.kind;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`Rectangle ${factorText(pl.a, k)} sur ${factorText(pl.b, k)} découpé en ${pl.pieces.length} morceaux : ${pl.pieces
        .map((x) => `${factorText(x.a, k)} × ${factorText(x.b, k)}`)
        .join(', ')}`}
      className={cn('border-ink-100 w-full max-w-[420px] rounded-2xl border', FIGURE_BG_CLASS)}
    >
      {pl.pieces.map((x) => {
        const x0 = xs[x.col]!;
        const y0 = ys[x.row]!;
        const w = xs[x.col + 1]! - x0;
        const h = ys[x.row + 1]! - y0;
        const two = w >= 66 && h >= 38 && !(single && hide);
        const one = w >= 30 && h >= 18;
        return (
          <g key={`${x.col},${x.row}`}>
            <rect
              x={x0}
              y={y0}
              width={w}
              height={h}
              fill={ROW_TONES[x.row % ROW_TONES.length]}
              stroke={STROKE}
              strokeWidth={1.6}
            />
            {(two || one) && (
              <text
                x={x0 + w / 2}
                y={y0 + h / 2 + (two ? -3 : 4)}
                fontSize={12}
                fontWeight={700}
                textAnchor="middle"
                fill={STROKE}
              >
                {two ? (
                  <>
                    <tspan x={x0 + w / 2}>{`${factorText(x.a, k)} × ${factorText(x.b, k)}`}</tspan>
                    <tspan x={x0 + w / 2} dy={15}>
                      {`= ${productText(x.p, k)}`}
                    </tspan>
                  </>
                ) : single && hide ? (
                  `${factorText(x.a, k)} × ${factorText(x.b, k)}`
                ) : (
                  productText(x.p, k)
                )}
              </text>
            )}
          </g>
        );
      })}
      {grid &&
        Array.from({ length: pl.a - 1 }, (_, i) => {
          const x = X0 + (i + 1) * scale;
          return (
            <line
              key={`v${i}`}
              x1={x}
              x2={x}
              y1={Y0}
              y2={bottom}
              stroke={STROKE}
              strokeOpacity={k === 'dec' && (i + 1) % 10 === 0 ? 0.6 : 0.16}
              strokeWidth={k === 'dec' && (i + 1) % 10 === 0 ? 1.4 : 1}
            />
          );
        })}
      {grid &&
        Array.from({ length: pl.b - 1 }, (_, i) => {
          const y = Y0 + (i + 1) * scale;
          return (
            <line
              key={`h${i}`}
              x1={X0}
              x2={right}
              y1={y}
              y2={y}
              stroke={STROKE}
              strokeOpacity={k === 'dec' && (i + 1) % 10 === 0 ? 0.6 : 0.16}
              strokeWidth={k === 'dec' && (i + 1) % 10 === 0 ? 1.4 : 1}
            />
          );
        })}
      {/* cotes des parts */}
      {pl.aParts.map((p, i) => (
        <text
          key={`a${i}`}
          x={(xs[i]! + xs[i + 1]!) / 2}
          y={Y0 - 10}
          fontSize={13}
          fontWeight={700}
          textAnchor="middle"
          fill={INK}
        >
          {factorText(p, k)}
        </text>
      ))}
      {pl.bParts.map((p, i) => (
        <text
          key={`b${i}`}
          x={X0 - 8}
          y={(ys[i]! + ys[i + 1]!) / 2 + 4}
          fontSize={13}
          fontWeight={700}
          textAnchor="end"
          fill={INK}
        >
          {factorText(p, k)}
        </text>
      ))}
      {!exact && (
        <text x={right} y={Math.min(H - 4, bottom + 16)} fontSize={12} textAnchor="end" fill={INK}>
          figure pas à l’échelle
        </text>
      )}
    </svg>
  );
}

/** multiplication posée, chaque ligne rattachée à sa rangée de morceaux */
function Posed({ pl, hide }: { pl: Plan; hide: boolean }) {
  const p = posed(pl);
  const k = pl.kind;
  const topText = factorText(pl.a, k);
  const bottomText = factorText(pl.b, k);
  const final = productText(pl.total, k);
  // une seule ligne : c'est déjà le produit
  const oneLine = p.lines.length === 1;
  const rows: Array<{ op: string; text: string; tone?: string; note?: string; bold?: boolean }> = [
    { op: '', text: topText },
    { op: '×', text: bottomText },
    ...p.lines.map((l, i) => {
      const inRow = pl.pieces.filter((x) => x.row === l.row);
      return {
        op: i > 0 ? '+' : '',
        text: hide && oneLine ? '?' : String(l.value),
        tone: ROW_TONES[l.row % ROW_TONES.length],
        note:
          k === 'int' && inRow.length > 1
            ? `= ${inRow.map((x) => productText(x.p, k)).join(' + ')}`
            : `(${l.label})`,
      };
    }),
    {
      op: '',
      text: hide ? '?' : p.result,
      bold: true,
      note: !hide && p.result !== final ? `= ${final}` : undefined,
    },
  ];
  return (
    <div className="bg-ink-50 rounded-2xl px-3 py-2">
      <p className="text-ink-700 mb-1 text-sm font-semibold">Multiplication posée</p>
      <table className="font-mono text-lg tabular-nums" aria-label="Multiplication posée">
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className={cn(
                (i === 1 || i === rows.length - 2) && 'border-ink-400 border-b',
                r.bold && 'font-bold',
              )}
            >
              <td className="text-ink-600 w-6 pr-1 text-right">{r.op}</td>
              <td className="text-ink-900 min-w-16 pr-2 text-right">
                {r.tone && (
                  <span
                    className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full align-middle"
                    style={{ background: r.tone }}
                    aria-hidden
                  />
                )}
                {r.text}
              </td>
              <td className="text-ink-600 pl-1 font-sans text-xs">{r.note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {k === 'dec' && !hide && (
        <p className="text-ink-700 mt-1 text-xs">{frenchSpaces(commaText(pl))}</p>
      )}
    </div>
  );
}

export function AreaMultiplicationTool({ sendCost, level }: ToolProps & { level?: string | null }) {
  const rank = Math.min(2, Math.max(1, schoolLevelRank(level) ?? 1));
  const [kind, setKind] = useState<Kind>('int');
  const [vals, setVals] = useState<Record<Kind, [number, number]>>({
    int: [23, 14],
    dec: [23, 14],
  });
  const [a, b] = vals[kind];
  const pl = plan(a, b, kind);
  const hide = sendCost > 0;
  const set = (i: 0 | 1, v: number) => setVals({ ...vals, [kind]: i === 0 ? [v, b] : [a, v] });
  const fmtF = (v: number) => factorText(v, kind);
  const single = pl.pieces.length === 1;
  const dist = distributeText(pl);
  const estimate = estimateText(pl);
  return (
    <div className="space-y-3">
      <Segmented
        ariaLabel="Nombres"
        value={kind}
        options={[
          { value: 'int', label: 'Entiers' },
          { value: 'dec', label: 'Décimaux' },
        ]}
        onChange={setKind}
      />
      <div className="grid grid-cols-2 gap-2">
        {([0, 1] as const).map((i) => (
          <NumberField
            key={`${kind}${i}`}
            label={i === 0 ? 'Premier facteur' : 'Second facteur'}
            value={(i === 0 ? a : b) / (kind === 'dec' ? 10 : 1)}
            min={kind === 'dec' ? 0.1 : 1}
            max={kind === 'dec' ? 9.9 : 99}
            step={kind === 'dec' ? 0.1 : 1}
            decimals={kind === 'dec' ? 1 : 0}
            onChange={(v) => set(i, Math.round(v * (kind === 'dec' ? 10 : 1)))}
          />
        ))}
      </div>
      <Rect pl={pl} hide={hide} />
      <Note live={false}>
        <span className="block">
          {frenchSpaces(`${splitText(a, kind)} ; ${splitText(b, kind)}.`)}
        </span>
        <span className="mt-1 block">
          {frenchSpaces(
            single
              ? 'Aucun facteur ne se découpe : le rectangle est d’un seul morceau.'
              : rank >= 2 && dist
                ? `Distributivité : ${dist}.`
                : `On découpe le rectangle en ${pl.pieces.length} morceaux et on additionne leurs aires.`,
          )}
        </span>
        <span className="mt-1 block font-semibold">
          {frenchSpaces(
            hide
              ? `${fmtF(a)} × ${fmtF(b)} = ${single ? '' : `${sumText(pl)} = `}? (calculatrice non autorisée : à toi de ${single ? 'calculer' : 'additionner les morceaux'})`
              : `${fmtF(a)} × ${fmtF(b)} = ${single ? '' : `${sumText(pl)} = `}${productText(pl.total, kind)}`,
          )}
        </span>
        {estimate && (
          <span className="text-ink-600 mt-1 block text-xs">{frenchSpaces(estimate)}</span>
        )}
      </Note>
      {!hide && (
        <p className="sr-only" aria-live="polite">
          {`${fmtF(a)} × ${fmtF(b)} = ${productText(pl.total, kind)}`}
        </p>
      )}
      <Posed pl={pl} hide={hide} />
    </div>
  );
}
